// src/pages/teams/EditTeam.jsx

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  doc, getDoc, updateDoc, setDoc, serverTimestamp, deleteField,
  collection, getDocs, query, orderBy,
} from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { useAuth } from '@/context/AuthContext';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import clsx from 'clsx';

import { Avatar } from '@/components/avatar';
import {
  PhotoIcon,
  ShieldCheckIcon,
  ArrowPathIcon,
  BuildingOffice2Icon,
  ArrowsRightLeftIcon,
  MapPinIcon,
  PlusIcon,
  ArrowTopRightOnSquareIcon,
  ChatBubbleLeftRightIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';
import SquareLogoUploader from '@/components/inputs/SquareLogoUploader';
import { uploadBlobToStorage } from '@/services/storageService';
// Owner-only "Transfer head coach" modal — same shape as the club one
// in ClubSettings, lives in src/components/teams/. Mounted at the bottom
// of the page; opens via the amber card we render after the form.
import TransferTeamOwnershipModal from '@/components/teams/TransferTeamOwnershipModal';
import TeamSubNav from '@/components/team/TeamSubNav';

const SPORTS     = ['Soccer', 'Basketball', 'Baseball', 'Softball', 'Football', 'Volleyball', 'Lacrosse', 'Hockey', 'Other'];
const AGE_GROUPS = ['U6', 'U7', 'U8', 'U9', 'U10', 'U11', 'U12', 'U13', 'U14', 'U15', 'U16', 'U17', 'U18', 'U19', 'Adult'];
const GENDERS    = ['Boys', 'Girls', 'Coed'];

/* ---------- Lime/pitch design tokens ---------- */
const HEAD_FONT = "'Barlow Condensed', sans-serif";

const INPUT_CLASSES =
  'block w-full rounded-xl border border-zinc-200 bg-white px-3.5 py-2.5 text-sm text-[#0d1f14] placeholder:text-zinc-400 transition focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30 focus:outline-none disabled:bg-zinc-50 disabled:text-zinc-500';

const INPUT_INVALID =
  'border-red-400 focus:border-red-400 focus:ring-red-200';

function SectionLabel({ children }) {
  return (
    <div
      className="text-xs font-semibold uppercase tracking-[0.18em] text-[#5a8a1f]"
      style={{ fontFamily: HEAD_FONT }}
    >
      {children}
    </div>
  );
}

function PrimaryButton({ children, className = '', disabled, ...props }) {
  return (
    <button
      disabled={disabled}
      className={clsx(
        'inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-bold uppercase tracking-wide text-[#0d1f14] shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all',
        'bg-[#b5f03d] hover:bg-[#94cc28]',
        'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-[#b5f03d]',
        className
      )}
      style={{ fontFamily: HEAD_FONT }}
      {...props}
    >
      {children}
    </button>
  );
}

function GhostButton({ children, className = '', ...props }) {
  return (
    <button
      type="button"
      className={clsx(
        'inline-flex items-center justify-center gap-2 rounded-xl border border-zinc-200 bg-white px-4 py-2 text-sm font-bold uppercase tracking-wide text-[#0d1f14] transition-all',
        'hover:border-[#b5f03d] hover:bg-[#b5f03d]/10',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className
      )}
      style={{ fontFamily: HEAD_FONT }}
      {...props}
    >
      {children}
    </button>
  );
}

function SectionCard({ children, title, icon: Icon, description }) {
  return (
    <div className="bg-white rounded-2xl border border-zinc-200 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] overflow-hidden">
      <div className="border-b border-zinc-100 px-6 py-4 flex items-start gap-3">
        <div className="p-2 rounded-lg bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 text-[#5a8a1f]">
          {Icon && <Icon className="w-5 h-5" />}
        </div>
        <div>
          <h3
            className="text-base font-bold uppercase tracking-wide text-[#0d1f14]"
            style={{ fontFamily: HEAD_FONT }}
          >
            {title}
          </h3>
          {description && <p className="mt-0.5 text-xs text-zinc-500">{description}</p>}
        </div>
      </div>
      <div className="px-6 py-5">{children}</div>
    </div>
  );
}

function FieldLabel({ children, htmlFor, required }) {
  return (
    <label
      htmlFor={htmlFor}
      className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.12em] text-[#0d1f14]"
      style={{ fontFamily: HEAD_FONT }}
    >
      {children} {required && <span className="text-red-500">*</span>}
    </label>
  );
}

function HelpText({ tone = 'muted', children }) {
  return (
    <p className={clsx('mt-1 text-xs', tone === 'error' ? 'text-red-500' : 'text-zinc-500')}>
      {children}
    </p>
  );
}

function SkeletonLoader() {
  return (
    <div className="mx-auto max-w-5xl p-6 space-y-8 animate-pulse">
      <div className="h-20 w-1/3 bg-zinc-200 rounded-lg" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 h-96 bg-zinc-100 rounded-2xl" />
        <div className="h-96 bg-zinc-100 rounded-2xl" />
      </div>
    </div>
  );
}

const initialsOf = (name = '?') =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('');

/* ---------- Main Component ---------- */
export default function EditTeam() {
  const { teamId } = useParams();
  const navigate   = useNavigate();
  const { firebaseUser } = useAuth();
  const { t } = useTranslation();

  const [form, setForm] = useState({
    name: '', sport: '', ageGroup: '', gender: '',
    // primaryLeague used to be a required field collected during the
    // setup wizard. We pulled it out of the wizard (it was a relic of
    // the scrimmage-matching era and confused brand-new coaches), but
    // we still let coaches enter it here optionally so the data path
    // stays open for when scrimmage matching ships back.
    primaryLeague: '',
    // `defaultHomeFieldId` points at one of the saved fields under
    // teams/{teamId}/fields/{fieldId}. Used by the event-creation modal
    // to pre-fill the location for new home games / practices so coaches
    // don't re-pick the same venue every week. Empty string = no default.
    defaultHomeFieldId: '',
    // `chatMode` controls how the team_announcement chat behaves:
    //   - 'open'      → everyone (coaches + parents) can post
    //   - 'broadcast' → only coaches/staff post; parents read-only
    // Missing field defaults to 'open' so new teams default to the
    // group-chat dynamic; clubs that prefer focused comms flip to
    // broadcast. Auto-saves on radio change (no separate save needed).
    chatMode: 'open',
    // `disableOverdueReminders` opts the team out of the weekly
    // overdue-payment reminder cron (functions/modules/overduePaymentReminders.js).
    // Default OFF (false) = the cron sends reminders. Some clubs prefer
    // to handle dunning themselves and want Scrimly to stay quiet.
    disableOverdueReminders: false,
  });
  // Saved venue library for THIS team — populated from the
  // teams/{teamId}/fields sub-collection that FieldsPage manages.
  // Drives the "Default home field" dropdown below the logo.
  const [teamFields, setTeamFields] = useState([]);
  const [logoPreview,    setLogoPreview]    = useState(null);
  const [processedLogo,  setProcessedLogo]  = useState(null);
  const [removeLogo,     setRemoveLogo]     = useState(false);
  const [loading,        setLoading]        = useState(true);
  const [saving,         setSaving]         = useState(false);
  const [attempted,      setAttempted]      = useState(false);
  const [errors,         setErrors]         = useState({});
  const [club,           setClub]           = useState(null);
  const clubRef          = useRef(null);
  const [clubSport,      setClubSport]      = useState(null);
  const [allowTeamLogos, setAllowTeamLogos] = useState(true);
  // Captured from the team doc so we can decide whether to show the
  // Transfer head coach card. Honor BOTH ownerId (canonical) and coachId
  // (legacy) to match useTeamAccess + ViewTeam.
  const [teamOwnerUid,   setTeamOwnerUid]   = useState(null);
  const [transferOpen,   setTransferOpen]   = useState(false);

  useEffect(() => {
    if (!teamId) return;
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, 'teams', teamId));
        if (!snap.exists()) throw new Error(t('editTeam.loadFailed'));
        const data = snap.data();
        setForm({
          name:               data.name               || '',
          sport:              data.sport              || '',
          ageGroup:           data.ageGroup           || '',
          gender:             data.gender             || '',
          primaryLeague:      data.primaryLeague      || '',
          defaultHomeFieldId: data.defaultHomeFieldId || '',
          // Default 'open' for any team that doesn't have the field yet
          // (existing teams created before chatMode shipped).
          chatMode:           data.chatMode === 'broadcast' ? 'broadcast' : 'open',
          // Default OFF — cron sends reminders unless explicitly opted out.
          disableOverdueReminders: data.disableOverdueReminders === true,
        });
        const clubData = data.clubId || data.clubName
          ? { id: data.clubId || null, name: data.clubName || '', slug: data.clubSlug || null }
          : null;
        setClub(clubData);
        clubRef.current = clubData;
        setTeamOwnerUid(data.ownerId || data.coachId || null);
        setLogoPreview(data.logoUrl || null);
        setClubSport(data.sportLockedByClub ? (data.sport || null) : null);
        setAllowTeamLogos(!data.logoLockedByClub);
        if (data.logoLockedByClub && data.clubLogoUrl && !data.logoUrl) {
          setLogoPreview(data.clubLogoUrl);
        }

        // Load the team's saved venue library in parallel with the team
        // doc above. Failure is non-fatal — the field dropdown just
        // shows the empty-state CTA, and the coach can still save the
        // rest of the team profile.
        try {
          const fieldsSnap = await getDocs(
            query(collection(db, 'teams', teamId, 'fields'), orderBy('name'))
          );
          setTeamFields(fieldsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (fieldsErr) {
          console.warn('[EditTeam] fields load skipped:', fieldsErr?.code, fieldsErr?.message);
        }
      } catch (err) {
        toast.error(err.message || t('editTeam.loadFailed'));
        navigate('/teams');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [teamId, navigate, t]);

  const missingMap = useMemo(() => ({
    name:     !form.name.trim(),
    sport:    !form.sport && !clubSport,
    ageGroup: !form.ageGroup,
    gender:   !form.gender,
  }), [form, clubSport]);

  const missingKeys = useMemo(
    () => Object.entries(missingMap).filter(([, v]) => v).map(([k]) => k),
    [missingMap],
  );

  const focusField = (key) => {
    const el = document.getElementById(key);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('ring-4', 'ring-[#b5f03d]/40');
      setTimeout(() => { el.focus?.(); el.classList.remove('ring-4', 'ring-[#b5f03d]/40'); }, 500);
    }
  };

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  }, [errors]);

  // Auto-save handler for chatMode — flips the radio AND writes the
  // single field to Firestore right away so the coach doesn't have to
  // remember to hit Save. Failures revert local state and toast so the
  // UI never lies about persisted state.
  const handleChatModeChange = useCallback(async (nextMode) => {
    const normalized = nextMode === 'broadcast' ? 'broadcast' : 'open';
    const previous = form.chatMode;
    if (normalized === previous) return;
    setForm((prev) => ({ ...prev, chatMode: normalized }));
    try {
      await updateDoc(doc(db, 'teams', teamId), {
        chatMode: normalized,
        updatedAt: serverTimestamp(),
      });
      toast.success(
        normalized === 'broadcast'
          ? t('editTeam.chat.toastBroadcast')
          : t('editTeam.chat.toastOpen')
      );
    } catch (err) {
      console.error('[EditTeam] chatMode save failed:', err);
      setForm((prev) => ({ ...prev, chatMode: previous }));
      toast.error(t('editTeam.chat.toastError'));
    }
  }, [form.chatMode, teamId, t]);

  // Auto-save for the overdue-reminder opt-out — same pattern as
  // chatMode: optimistic UI flip, single-field Firestore write,
  // rollback on failure. The weekly cron at functions/modules/
  // overduePaymentReminders.js reads this field and skips the team
  // when it's true.
  const handleOverdueRemindersChange = useCallback(async (nextEnabled) => {
    // The UI shows "Send overdue reminders" (default ON), so the
    // STORED field is the inverse: disableOverdueReminders is true
    // when the toggle is OFF. Translate at the boundary.
    const nextDisabled = !nextEnabled;
    const previousDisabled = form.disableOverdueReminders === true;
    if (nextDisabled === previousDisabled) return;
    setForm((prev) => ({ ...prev, disableOverdueReminders: nextDisabled }));
    try {
      await updateDoc(doc(db, 'teams', teamId), {
        disableOverdueReminders: nextDisabled,
        updatedAt: serverTimestamp(),
      });
      toast.success(
        nextDisabled
          ? t('editTeam.reminders.toastOff')
          : t('editTeam.reminders.toastOn')
      );
    } catch (err) {
      console.error('[EditTeam] disableOverdueReminders save failed:', err);
      setForm((prev) => ({ ...prev, disableOverdueReminders: previousDisabled }));
      toast.error(t('editTeam.reminders.toastError'));
    }
  }, [form.disableOverdueReminders, teamId, t]);

  const validate = useCallback(() => {
    const e = {};
    if (!form.name.trim())          e.name     = t('editTeam.details.nameRequired');
    if (!form.sport && !clubSport)  e.sport    = t('editTeam.details.sportRequired');
    if (!form.ageGroup)             e.ageGroup = t('editTeam.details.ageRequired');
    if (!form.gender)               e.gender   = t('editTeam.details.genderRequired');
    setErrors(e);
    return Object.keys(e).length === 0;
  }, [form, clubSport, t]);

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setAttempted(true);
    if (!validate()) {
      focusField(missingKeys[0]);
      return;
    }

    setSaving(true);
    const toastId = toast.loading(t('editTeam.savingToast'));
    try {
      const resolvedSport = clubSport || form.sport;

      // Resolve the picked field → store both id (canonical) and name
      // (denormalized) so event lists can show the venue label without
      // joining back to the fields sub-collection on every render.
      // Empty string means "no default" — clear both via deleteField()
      // so we don't leave a dangling stale name.
      const pickedField = teamFields.find(f => f.id === form.defaultHomeFieldId) || null;

      const updates = {
        name:          form.name.trim(),
        sport:         resolvedSport,
        ageGroup:      form.ageGroup,
        gender:        form.gender,
        // Optional. Empty string is fine; we only store something
        // when the coach actually filled it in.
        primaryLeague: form.primaryLeague?.trim() || '',
        updatedAt:     serverTimestamp(),
        setupComplete: true,
        profileStatus: 'complete',
        defaultHomeFieldId:   pickedField ? pickedField.id   : deleteField(),
        defaultHomeFieldName: pickedField ? (pickedField.name || '') : deleteField(),
        // Persist team chat mode (open vs broadcast). Default 'open'
        // matches the group-chat dynamic; flip to 'broadcast' to
        // restrict posting to coaches/staff only.
        chatMode:      form.chatMode === 'broadcast' ? 'broadcast' : 'open',
        // Persist overdue-reminder opt-out. Default false (= cron
        // sends reminders); some clubs prefer to handle dunning
        // themselves and toggle this on.
        disableOverdueReminders: form.disableOverdueReminders === true,
      };

      if (processedLogo?.blob) {
        const path = `teamLogos/${teamId}/logo_${Date.now()}.webp`;
        const { downloadURL } = await uploadBlobToStorage(processedLogo.blob, path, {
          contentType: processedLogo.blob.type,
        });
        updates.logoUrl = downloadURL;
      } else if (removeLogo) {
        updates.logoUrl = deleteField();
      }

      await updateDoc(doc(db, 'teams', teamId), updates);

      // team_index is a public search-index mirror; client writes are
      // blocked by Firestore rules (a CF trigger keeps it in sync server-
      // side). Don't fail the save if the optional index write is rejected
      // — matches the try/catch pattern in CreateTeams.jsx.
      try {
        await setDoc(
          doc(db, 'team_index', teamId),
          {
            name_lower: form.name.trim().toLowerCase(),
            sport:      resolvedSport,
            ageGroup:   form.ageGroup,
            gender:     form.gender,
            clubId:     clubRef.current?.id ?? null,
            updatedAt:  serverTimestamp(),
          },
          { merge: true },
        );
      } catch (idxErr) {
        console.warn('[EditTeam] team_index write skipped:', idxErr?.code, idxErr?.message);
      }

      if (firebaseUser?.uid) {
        await updateDoc(doc(db, 'users', firebaseUser.uid), {
          mustCompleteTeamId: deleteField(),
          updatedAt:          serverTimestamp(),
        });
      }

      toast.success(t('editTeam.savedToast'), { id: toastId });
      navigate(`/teams/${teamId}`, { replace: true });
    } catch (err) {
      console.error(err);
      toast.error(t('editTeam.saveFailed'), { id: toastId });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <SkeletonLoader />;

  return (
    <div className="mx-auto max-w-5xl p-4 sm:p-6 lg:p-8 min-h-screen">
      {/* Team sub-nav: keeps Overview/Roster/Schedule/Chat tabs visible while drilled into team-scoped content. */}
      <TeamSubNav teamId={teamId} />
      {/* Page header */}
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <SectionLabel>{t('editTeam.eyebrow')}</SectionLabel>
          <h1
            className="mt-1 text-2xl sm:text-3xl font-bold uppercase tracking-tight text-[#0d1f14]"
            style={{ fontFamily: HEAD_FONT }}
          >
            {t('editTeam.title')}
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            {t('editTeam.subtitle', { name: form.name || t('editTeam.thisTeam') })}
          </p>
        </div>
        <div className="flex gap-3 shrink-0">
          <GhostButton onClick={() => navigate(-1)} disabled={saving}>{t('editTeam.cancel')}</GhostButton>
          <PrimaryButton type="submit" form="edit-team-form" disabled={saving}>
            {saving ? <ArrowPathIcon className="animate-spin w-4 h-4" /> : null}
            {saving ? t('editTeam.saving') : t('editTeam.save')}
          </PrimaryButton>
        </div>
      </div>

      <form id="edit-team-form" onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">

          {/* Left — Core Details + Location */}
          <div className="lg:col-span-2 space-y-8">
            <SectionCard
              title={t('editTeam.details.title')}
              icon={ShieldCheckIcon}
              description={t('editTeam.details.desc')}
            >
              <div className="space-y-5">
                {/* Team name */}
                <div>
                  <FieldLabel htmlFor="name" required>{t('editTeam.details.nameLabel')}</FieldLabel>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={form.name}
                    onChange={handleChange}
                    disabled={saving}
                    placeholder={t('editTeam.details.namePlaceholder')}
                    className={clsx(INPUT_CLASSES, attempted && errors.name && INPUT_INVALID)}
                  />
                  {attempted && errors.name
                    ? <HelpText tone="error">{errors.name}</HelpText>
                    : <HelpText>{t('editTeam.details.nameHelp')}</HelpText>
                  }
                </div>

                {/* Sport — locked if club has a primary sport */}
                <div>
                  <FieldLabel htmlFor="sport" required>{t('editTeam.details.sportLabel')}</FieldLabel>
                  {clubSport ? (
                    <>
                      <div className="flex items-center gap-2.5 rounded-xl border border-zinc-200 bg-zinc-50 px-3.5 py-2.5">
                        <span className="text-sm font-medium text-[#0d1f14]">{t(`editTeam.sports.${clubSport}`, clubSport)}</span>
                        <span
                          className="ml-auto text-[10px] text-zinc-400 uppercase tracking-wide font-bold"
                          style={{ fontFamily: HEAD_FONT }}
                        >
                          {t('editTeam.details.setByClub')}
                        </span>
                      </div>
                      <HelpText>{t('editTeam.details.sportLockedHelp')}</HelpText>
                    </>
                  ) : (
                    <>
                      <select
                        id="sport"
                        name="sport"
                        value={form.sport}
                        onChange={handleChange}
                        disabled={saving}
                        className={clsx(INPUT_CLASSES, attempted && errors.sport && INPUT_INVALID)}
                      >
                        <option value="">{t('editTeam.details.selectSport')}</option>
                        {SPORTS.map((s) => <option key={s} value={s}>{t(`editTeam.sports.${s}`, s)}</option>)}
                      </select>
                      {attempted && errors.sport && <HelpText tone="error">{errors.sport}</HelpText>}
                    </>
                  )}
                </div>

                {/* Age + Gender */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <FieldLabel htmlFor="ageGroup" required>{t('editTeam.details.ageLabel')}</FieldLabel>
                    <select
                      id="ageGroup"
                      name="ageGroup"
                      value={form.ageGroup}
                      onChange={handleChange}
                      disabled={saving}
                      className={clsx(INPUT_CLASSES, attempted && errors.ageGroup && INPUT_INVALID)}
                    >
                      <option value="">{t('editTeam.details.select')}</option>
                      {AGE_GROUPS.map((a) => <option key={a} value={a}>{a === 'Adult' ? t('editTeam.adult') : a}</option>)}
                    </select>
                    {attempted && errors.ageGroup && <HelpText tone="error">{errors.ageGroup}</HelpText>}
                  </div>
                  <div>
                    <FieldLabel htmlFor="gender" required>{t('editTeam.details.genderLabel')}</FieldLabel>
                    <select
                      id="gender"
                      name="gender"
                      value={form.gender}
                      onChange={handleChange}
                      disabled={saving}
                      className={clsx(INPUT_CLASSES, attempted && errors.gender && INPUT_INVALID)}
                    >
                      <option value="">{t('editTeam.details.select')}</option>
                      {GENDERS.map((g) => <option key={g} value={g}>{t(`editTeam.genders.${g}`, g)}</option>)}
                    </select>
                    {attempted && errors.gender && <HelpText tone="error">{errors.gender}</HelpText>}
                  </div>
                </div>

                {/* Optional. Was a required wizard step originally; coaches
                    asked why we needed it before they'd even built the team,
                    so we moved it here. Useful if scrimmage matching ships
                    back and we need to pair teams in the same league. */}
                <div className="mt-4">
                  <FieldLabel htmlFor="primaryLeague">
                    {t('editTeam.details.leagueLabel')} <span className="text-zinc-400 font-normal">{t('editTeam.details.optional')}</span>
                  </FieldLabel>
                  <input
                    id="primaryLeague"
                    name="primaryLeague"
                    type="text"
                    value={form.primaryLeague}
                    onChange={handleChange}
                    disabled={saving}
                    placeholder={t('editTeam.details.leaguePlaceholder')}
                    className={INPUT_CLASSES}
                  />
                </div>
              </div>
            </SectionCard>

            {/* ── Team chat mode ──────────────────────────────
                Two-radio control wired to the team doc's `chatMode`
                field. Auto-saves on change (no separate Save button)
                so coaches can flip and forget. The chat composer
                reads this field at render time — switching here
                instantly hides/shows the parent composer in the
                team announcement channel. */}
            <SectionCard
              title={t('editTeam.chat.title')}
              icon={ChatBubbleLeftRightIcon}
              description={t('editTeam.chat.desc')}
            >
              <div className="space-y-3">
                {/* Open group chat */}
                <label
                  className={clsx(
                    'flex gap-3 rounded-xl border p-4 cursor-pointer transition-colors',
                    form.chatMode === 'open'
                      ? 'border-[#b5f03d] bg-[#b5f03d]/10 ring-2 ring-[#b5f03d]/30'
                      : 'border-zinc-200 bg-white hover:border-[#b5f03d]/40 hover:bg-[#b5f03d]/5'
                  )}
                >
                  <input
                    type="radio"
                    name="chatMode"
                    value="open"
                    checked={form.chatMode === 'open'}
                    onChange={() => handleChatModeChange('open')}
                    disabled={saving}
                    className="mt-1 h-4 w-4 accent-[#b5f03d] flex-shrink-0"
                  />
                  <div className="min-w-0">
                    <p
                      className="text-sm font-bold uppercase tracking-wide text-[#0d1f14]"
                      style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                    >
                      {t('editTeam.chat.openTitle')}
                    </p>
                    <p className="mt-0.5 text-xs text-zinc-500">
                      {t('editTeam.chat.openDesc')}
                    </p>
                  </div>
                </label>

                {/* Broadcast only */}
                <label
                  className={clsx(
                    'flex gap-3 rounded-xl border p-4 cursor-pointer transition-colors',
                    form.chatMode === 'broadcast'
                      ? 'border-[#b5f03d] bg-[#b5f03d]/10 ring-2 ring-[#b5f03d]/30'
                      : 'border-zinc-200 bg-white hover:border-[#b5f03d]/40 hover:bg-[#b5f03d]/5'
                  )}
                >
                  <input
                    type="radio"
                    name="chatMode"
                    value="broadcast"
                    checked={form.chatMode === 'broadcast'}
                    onChange={() => handleChatModeChange('broadcast')}
                    disabled={saving}
                    className="mt-1 h-4 w-4 accent-[#b5f03d] flex-shrink-0"
                  />
                  <div className="min-w-0">
                    <p
                      className="text-sm font-bold uppercase tracking-wide text-[#0d1f14]"
                      style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                    >
                      {t('editTeam.chat.broadcastTitle')}
                    </p>
                    <p className="mt-0.5 text-xs text-zinc-500">
                      {t('editTeam.chat.broadcastDesc')}
                    </p>
                  </div>
                </label>
              </div>
            </SectionCard>

            {/* Overdue payment reminders — UI for the disableOverdueReminders
                team-doc field. The weekly cron at functions/modules/
                overduePaymentReminders.js (Mondays 09:00) auto-emails
                parents whose flat-fee registration is 7+ days overdue.
                Default ON (cron sends). Some clubs prefer to handle
                dunning themselves and want Scrimly to stay quiet.
                Auto-saves on toggle (no separate Save button). */}
            <SectionCard
              title={t('editTeam.reminders.title')}
              icon={CurrencyDollarIcon}
              description={t('editTeam.reminders.desc')}
            >
              <label className="flex items-start gap-3 cursor-pointer">
                <button
                  type="button"
                  role="switch"
                  aria-checked={!form.disableOverdueReminders}
                  onClick={() => handleOverdueRemindersChange(form.disableOverdueReminders === true)}
                  disabled={saving}
                  className={clsx(
                    'relative mt-0.5 h-6 w-11 shrink-0 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#b5f03d]/60 focus:ring-offset-1',
                    !form.disableOverdueReminders ? 'bg-[#5a8a1f]' : 'bg-zinc-300'
                  )}
                >
                  <span
                    className={clsx(
                      'absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform',
                      !form.disableOverdueReminders ? 'translate-x-5' : 'translate-x-0'
                    )}
                  />
                </button>
                <div className="min-w-0">
                  <p
                    className="text-sm font-bold uppercase tracking-wide text-[#0d1f14]"
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                  >
                    {t('editTeam.reminders.toggleTitle')}
                  </p>
                  <p className="mt-0.5 text-xs text-zinc-500">
                    {t('editTeam.reminders.toggleDesc')}
                  </p>
                </div>
              </label>
            </SectionCard>
          </div>

          {/* Right — Identity */}
          <div className="space-y-8">
            <SectionCard title={t('editTeam.identity.title')} icon={PhotoIcon}>
              <div className="flex flex-col items-center">
                <Avatar
                  src={logoPreview}
                  initials={initialsOf(form.name)}
                  className="!h-32 !w-32 mb-6 shadow-2xl ring-4 ring-zinc-50"
                  square
                />

                {allowTeamLogos ? (
                  <div className="w-full space-y-3">
                    <SquareLogoUploader
                      label={logoPreview ? t('editTeam.identity.updateLogo') : t('editTeam.identity.uploadLogo')}
                      initialPreview={logoPreview}
                      onProcessed={(res) => {
                        setProcessedLogo(res);
                        setLogoPreview(res.dataUrl);
                        setRemoveLogo(false);
                      }}
                    />
                    {logoPreview && (
                      <button
                        type="button"
                        onClick={() => {
                          setLogoPreview(null);
                          setProcessedLogo(null);
                          setRemoveLogo(true);
                        }}
                        className="w-full text-xs font-bold text-red-500 uppercase tracking-widest hover:text-red-600 transition"
                        style={{ fontFamily: HEAD_FONT }}
                      >
                        {t('editTeam.identity.removeLogo')}
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="w-full rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-center">
                    <p className="text-xs font-bold uppercase tracking-wide text-amber-700" style={{ fontFamily: HEAD_FONT }}>
                      {t('editTeam.identity.logoManagedTitle')}
                    </p>
                    <p className="text-xs text-amber-600 mt-0.5">
                      {t('editTeam.identity.logoManagedDesc')}
                    </p>
                  </div>
                )}
              </div>

              {/* Club affiliation */}
              <div className="mt-8 pt-8 border-t border-zinc-100">
                <FieldLabel>{t('editTeam.identity.clubLabel')}</FieldLabel>
                {club ? (
                  <div className="flex items-center gap-2.5 rounded-xl border border-zinc-200 bg-zinc-50 px-3.5 py-2.5">
                    <BuildingOffice2Icon className="h-4 w-4 text-zinc-400 shrink-0" />
                    <span className="text-sm text-[#0d1f14] font-medium">{club.name}</span>
                    <span
                      className="ml-auto text-[10px] text-zinc-400 uppercase tracking-wide font-bold"
                      style={{ fontFamily: HEAD_FONT }}
                    >
                      {t('editTeam.identity.managedByClub')}
                    </span>
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed border-zinc-200 px-3.5 py-2.5 text-sm text-zinc-400">
                    {t('editTeam.identity.noClub')}
                  </div>
                )}
                <HelpText>{t('editTeam.identity.clubHelp')}</HelpText>
              </div>
            </SectionCard>

            {/* ── Home Field ───────────────────────────────────
                The team's "where do you usually play?" default. When set,
                the event-creation modal pre-fills this field for new
                games/practices so coaches stop re-typing their home venue
                every week. The full venue library (multi-field add/edit)
                lives at /teams/:teamId/fields — that's where coaches go
                to add a NEW field; this card is just for picking which of
                the existing ones is the default. */}
            <SectionCard
              title={t('editTeam.field.title')}
              icon={MapPinIcon}
              description={t('editTeam.field.desc')}
            >
              {teamFields.length === 0 ? (
                // Empty state — no saved fields yet. Drive coaches to the
                // dedicated venue library page to add their first field
                // rather than building an inline add form here.
                <div className="rounded-xl border border-dashed border-zinc-200 bg-[#f7faf5] px-4 py-5 text-center">
                  <MapPinIcon className="h-6 w-6 text-zinc-400 mx-auto mb-2" />
                  <p className="text-sm font-bold text-[#0d1f14]" style={{ fontFamily: HEAD_FONT }}>
                    {t('editTeam.field.emptyTitle')}
                  </p>
                  <p className="text-xs text-zinc-500 mt-1 mb-4">
                    {t('editTeam.field.emptyDesc')}
                  </p>
                  <Link
                    to={`/teams/${teamId}/fields`}
                    className="inline-flex items-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 py-2 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97]"
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                  >
                    <PlusIcon className="h-4 w-4" />
                    {t('editTeam.field.addFirst')}
                  </Link>
                </div>
              ) : (
                <>
                  <FieldLabel htmlFor="defaultHomeFieldId">{t('editTeam.field.defaultLabel')}</FieldLabel>
                  <select
                    id="defaultHomeFieldId"
                    name="defaultHomeFieldId"
                    value={form.defaultHomeFieldId}
                    onChange={handleChange}
                    disabled={saving}
                    className={INPUT_CLASSES}
                  >
                    <option value="">{t('editTeam.field.noDefault')}</option>
                    {teamFields.map(f => (
                      <option key={f.id} value={f.id}>
                        {f.name}{f.address ? ` · ${f.address}` : ''}
                      </option>
                    ))}
                  </select>
                  <HelpText>
                    {t('editTeam.field.defaultHelp')}
                  </HelpText>

                  <div className="mt-4 pt-4 border-t border-zinc-100">
                    <Link
                      to={`/teams/${teamId}/fields`}
                      className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-[#5a8a1f] hover:text-[#3d6010] transition-colors"
                      style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                    >
                      <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                      {t('editTeam.field.yourFields', { count: teamFields.length })}
                    </Link>
                  </div>
                </>
              )}
            </SectionCard>
          </div>
        </div>
      </form>

      {/* ── Transfer head coach ─────────────────────────
          Owner-only escape hatch sitting BELOW the form. Same shape as the
          club ownership transfer card in ClubSettings — amber styling
          flags the destructive nature. Only renders when:
          • the team has loaded (we know the current owner uid)
          • the current viewer IS that owner (not staff or club director)
          Hidden for staff so the page stays clean for them. Self-service
          required by the deleteMyAccount pre-flight check, which refuses
          to delete an account that still owns teams. */}
      {teamOwnerUid && firebaseUser?.uid === teamOwnerUid && (
        <div className="mt-8 rounded-2xl border border-amber-200 bg-amber-50 p-6 space-y-3">
          <div className="flex items-center gap-2">
            <ArrowsRightLeftIcon className="h-5 w-5 text-amber-700" />
            <h2
              className="text-[11px] font-bold uppercase tracking-[1.4px] text-amber-800"
              style={{ fontFamily: HEAD_FONT }}
            >
              {t('editTeam.transfer.title')}
            </h2>
          </div>
          <p className="text-sm text-zinc-700">
            {t('editTeam.transfer.descPart1')}<span className="font-semibold text-[#0d1f14]">{t('editTeam.transfer.staff')}</span>{t('editTeam.transfer.descPart2')}
          </p>
          <button
            type="button"
            onClick={() => setTransferOpen(true)}
            className="inline-flex items-center gap-2 rounded-xl border border-amber-300 bg-white px-4 py-2 text-sm font-bold uppercase text-amber-800 hover:bg-amber-100 transition-colors"
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
          >
            <ArrowsRightLeftIcon className="h-4 w-4" />
            {t('editTeam.transfer.title')}
          </button>
        </div>
      )}

      {/* Owner-only handoff modal. Hidden when not the owner. After a
          successful transfer the user is no longer owner → bounce to the
          team page so the role context reloads with the new permissions. */}
      {teamOwnerUid && firebaseUser?.uid === teamOwnerUid && (
        <TransferTeamOwnershipModal
          open={transferOpen}
          onClose={() => setTransferOpen(false)}
          team={{ id: teamId, name: form.name }}
          currentOwnerUid={firebaseUser.uid}
          onTransferred={() => navigate(`/teams/${teamId}`)}
        />
      )}
    </div>
  );
}
